/* eslint-disable default-param-last */
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffectOnce } from '@/hooks';
import { PRODUCT_TYPE } from '@constants';
import { getShoplazzaBPPolicyListData, getPolicyListData } from './getPolicyList';
import { SESSION_KEY, SPECIAL_MERCHANTS } from './constants';
import { debounce } from '../../utils';

// policyType 映射
const policyTypeMap = {
  Garmentory: {
    [PRODUCT_TYPE.Return]: 'garmentory_ra',
    [PRODUCT_TYPE.Buyer]: 'garmentory_bp',
    [PRODUCT_TYPE.Assured]: 'garmentory_as',
  },
  Shopify: {
    [PRODUCT_TYPE.Return]: 'shopify_ra',
    [PRODUCT_TYPE.Buyer]: 'shopify_bp',
    [PRODUCT_TYPE.Assured]: 'shopify_as',
  },
};

// shoplazza bp特殊 其他皆走统一接口list-merchant-policies 根据policyType区分
const getPolicyListApiName = (code, name) => {
  if (name === 'Shoplazza') {
    return getShoplazzaBPPolicyListData;
  }
  return getPolicyListData;
};

/** 列表一页展示的数据量 */
const INITIAL_PER_PAGE = 20;

/** 翻页 */
const handleChangePage = ({
  newPage, searchValue, setPage, fetchPolicyList,
}) => {
  setPage(newPage);
  // if (newPage <= loadedPage.current) {
  //   setDisplayPage(newPage);
  // } else {
  fetchPolicyList(newPage, searchValue);
  // }
};

const handleChangeRowsPerPage = (event, { setRowsPerPage, setPage }) => {
  setRowsPerPage(+event.target.value);
  setPage(0);
};

/**
 * 处理 policy list 逻辑
 * @param {*} tab 需要展示的 tab
 * @param {*} tabIndex 当前展示的 tab 的 index
 * @param {*} index 需要展示的 tab 的 index
 */
export const usePolicyList = (tab = {}, tabIndex, index, searchValue, name, info) => {
  const [page, setPage] = useState(0);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(INITIAL_PER_PAGE);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  // const loadedPage = useRef(page);
  const isInit = useRef(false);
  const navigate = useNavigate();

  const debounceData = debounce(async (newPage = page, search) => {
    isInit.current = false;
    if (!tab.code) return;
    let type = policyTypeMap[name] && policyTypeMap[name][tab.code];
    const isSpecialMerchant = SPECIAL_MERCHANTS.includes(info);
    // 针对特殊shopify店铺不做筛选。起因后端数据新老不兼容
    // 针对shopify hardcode  当前shopify ra /shopify poizon 皆只有一个产品，故查all products时暂时赋值查当前开通产品的policy 原因：后端不支持不指定policyType  allen 知晓
    if (!type && name === 'Shopify' && !isSpecialMerchant) {
      type = info.indexOf('poizon') > -1 ? 'shopify_bp' : 'shopify_ra';
    }

    const body = {
      page: newPage,
      pageSize: rowsPerPage,
      queryParam: search || undefined,
      policyType: type,
    };

    setIsLoading(true);
    let res = {
      list: [],
      totalCount: 0,
    };
    // 当shopify时，聚合ra和bp 因为接口不支持搜索全部
    if (tab.code === 'all' && name === 'Shopify' && !isSpecialMerchant) {
      const values = await Promise.all([getPolicyListApiName(tab.code, name)({ ...body, policyType: 'shopify_ra' }, {
        handleSessionExpired: () => navigate('/login'),
      }), getPolicyListApiName(tab.code, name)({ ...body, policyType: 'shopify_bp' }, {
        handleSessionExpired: () => navigate('/login'),
      })]);
      console.log(values, 'values');
      values.forEach(({ list = [], totalCount = 0 }) => {
        res.list.push(...list);
        res.totalCount += totalCount;
      });
      // 按照时间排序
      res.list.sort((a, b) => new Date(b.policyCreateDate).getTime() - new Date(a.policyCreateDate).getTime());
    } else {
      res = await getPolicyListApiName(tab.code, name)(body, {
        handleSessionExpired: () => navigate('/login'),
      });
    }

    if (!res) {
      if (!rows.length) {
        setIsEmpty(true);
      }
      setIsLoading(false);
      return;
    }

    const { list = [], totalCount } = res;
    // loadedPage.current = loadedPage.current <= currentPage ? currentPage : loadedPage.current;

    if (list.length === 0) {
      setIsEmpty(true);
      setRows([]);

      setIsLoading(false);
      return;
    }
    setIsEmpty(false);
    setRows(list);

    setTotal(totalCount);
    setIsLoading(false);
  }, 500);

  /**
 * 拉取 policy 列表数据
 */
  const fetchPolicyList = useCallback(debounceData, [tabIndex]);

  /**
 * 初始化数据
 * 如果 sessionStorage 里有缓存的page，取缓存page
 * 目的：从 policy detail 页面返回时需要保存列表page和searchValue
 */
  const initData = () => {
    let data = null;
    try {
      data = JSON.parse(sessionStorage.getItem(SESSION_KEY.POLICY_LIST));
    } catch (e) {
      data = null;
    }

    let {
      page: lastPage = page,
      // loadedPage: lastLoadedPage,
    } = data || {};
    if (searchValue) {
      lastPage = 0;
    }
    setPage(lastPage);
    const search = searchValue;
    fetchPolicyList(lastPage, search);

    // loadedPage.current = lastLoadedPage;
  };

  /** 将当前状态保存在 sessionStorage 中 */
  const saveData = () => {
    sessionStorage.setItem(SESSION_KEY.POLICY_LIST, JSON.stringify({
      page,
      // isEmpty,
      // isLoading,
      // rowsPerPage,
      // rows,
      // total,
      // loadedPage: loadedPage.current,
    }));
    sessionStorage.setItem(SESSION_KEY.POLICY_SEARCHVALUE, searchValue || '');
    sessionStorage.setItem(SESSION_KEY.POLICY_LIST_TABINDEX, tabIndex);
  };

  /** 清除保存的状态 */
  const clearData = () => {
    sessionStorage.setItem(SESSION_KEY.POLICY_LIST, null);
  };

  /**
 * 跳转到详情页
 * @param {String} policyId  保单ID
 */

  const handleClickPolicy = (policyId, policyType, code) => {
    if (name === 'Shoplazza') {
      return;
    }
    saveData();
    navigate(`./${policyId}`, {
      state: {
        type: code,
        policyType,
      },
    });
  };

  useEffectOnce(() => {
    if (tabIndex !== index) {
      return;
    }
    isInit.current = true;
    // mount 的时候从 sessionStorage 拿数据
    initData();
    clearData();
  });

  useEffect(() => {
    if (searchValue) {
      isInit.current = false;
    }
    if (tabIndex === index && !isInit.current) {
      initData();
      clearData();
    }
  }, [tabIndex, searchValue]);

  return {
    isEmpty,
    isLoading,
    total,
    rows,
    page,
    rowsPerPage,
    handleChangePage: (event, newPage) => handleChangePage({
      newPage, searchValue, setPage, fetchPolicyList,
    }),
    handleChangeRowsPerPage: (event) => handleChangeRowsPerPage(event, { setRowsPerPage, setPage }),
    handleClickPolicy,
    fetchPolicyList,
  };
};
